import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputLabel,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CardElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CrossIcon from "assets/icons/cross.svg";
import DarkLogo from "assets/icons/dark-logo.svg";
import { useEffect, useState } from "react";
import {
  stripeCardElementOptions,
  paymentCheckoutModalStyles as styles,
} from "styles/Checkout/paymentCheckoutModal.styles";

const stripePromise = loadStripe(
  "pk_live_51MdZa2LKrWeQb9h6r8GcfqKtPUkxWXq1wbvltrxcBkmLHVphsW4v8snpjRcBYwWVdlpq6xT9UOjifUb7oX7jKVHY00l9Hxt1aZ"
);

// const stripePromise = loadStripe(
//   "pk_test_51MdZa2LKrWeQb9h6YVtleEP2vxe9nyRbCgaCvtx8RR8OIQFzEwgKHF0uLMuNuXStX8AKsZsjrvEyQplZubsj5t3z00I4e73zQJ"
// );

interface CheckoutModalProps {
  setShowCheckout: any;
  price: number;
  currencyFormat: any;
  handleCheckoutClick: any;
  handleGetStripeClientSecret: any;
  setShowSuccess: any;
  setName: any;
  setPhoneNumber: any;
  setEmail: any;
  name: string;
  phoneNumber: string;
  email: string;
  setIsLoading: any;
}

const CheckoutForm = ({
  setShowCheckout,
  price,
  currencyFormat,
  handleGetStripeClientSecret,
  handleCheckoutClick,
  setShowSuccess,
  setName,
  setPhoneNumber,
  setEmail,
  name,
  phoneNumber,
  email,
  setIsLoading,
}: CheckoutModalProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [showApplePay, setShowApplePay] = useState(false);

  useEffect(() => {
    if (!stripe) return;

    // Initialize payment request for Apple Pay / Google Pay
    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: "Total", amount: Math.round(price * 1.06 * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if Apple Pay / Google Pay is available
    pr.canMakePayment().then((result) => {
      if (result) {
        setShowApplePay(true);
        setPaymentRequest(pr);
      }
    });
  }, [stripe, price]);
  const calculateFees = (value: number) => {
    const fees = value * 0.06;
    return fees;
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    console.log("checkoutClicked");
    event.preventDefault();
    setLoading(true);
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    const clientSecret = await handleGetStripeClientSecret();
    
    try {
      if (clientSecret.token) {
        const result = await stripe.confirmCardPayment(clientSecret.token, {
          payment_method: {
            card: cardElement!,
            billing_details: {
              name,
              email,
              phone: phoneNumber,
            },
          },
        });
        console.log(result);
        if (result.error) {
          window.alert(result.error.message);
          setLoading(false);
        } else {
          const checkedOut = await handleCheckoutClick(clientSecret.id);
          setLoading(false);
          setShowSuccess(true);
        }
        setIsLoading(false);
        setLoading(false);
      } else {
        setLoading(false);
        setIsLoading(false);
        setShowSuccess(false);
      }
    } catch (ex) {
      console.error("e", JSON.stringify(ex));
    }
  };

  return (
    <Box className="modal-overlay" sx={styles.modalOverlay(theme)}>
      <Box className="modal-content" sx={styles.modalContent}>
        <Box className="modal-header">
          <img
            src={CrossIcon}
            alt="Close"
            style={styles.crossIcon}
            onClick={() => window.location.reload()}
          />
        </Box>

        <Typography sx={styles.title(theme)}>Payment Information</Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={styles.inputContainer}>
            <InputLabel sx={styles.inputLabel(theme)}>Full Name *</InputLabel>
            <TextField
              fullWidth
              required
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={styles.textField}
            />
          </Box>

          <Box sx={styles.inputContainer}>
            <InputLabel sx={styles.inputLabel(theme)}>
              Phone Number *
            </InputLabel>
            <TextField
              inputProps={{ type: "number", pattern: "[0-9]*" }}
              fullWidth
              required
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(e) => {
                // Ensure only digits are entered & restrict to max 10 characters
                const numericValue = e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 10);
                setPhoneNumber(numericValue);
              }}
              sx={styles.textField}
            />
          </Box>

          <Box sx={styles.inputContainer}>
            <InputLabel sx={styles.inputLabel(theme)}>
              Email Address *
            </InputLabel>
            <TextField
              fullWidth
              required
              placeholder="example@example.com"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={styles.textField}
            />
          </Box>

          {/* Apple Pay / Google Pay Button */}
          {showApplePay && paymentRequest && (
            <Box sx={styles.applePayButtonContainer}>
              <PaymentRequestButtonElement options={{ paymentRequest }} />
            </Box>
          )}

          {/* Card Details Field */}
          <Box sx={styles.inputContainer}>
            <InputLabel sx={styles.inputLabel(theme)}>
              Card Details *
            </InputLabel>
            <Box sx={styles.cardElementContainer}>
              <CardElement options={stripeCardElementOptions} />
            </Box>
          </Box>

          <Divider sx={styles.divider} />

          {/* Payment Button */}
          <Box sx={styles.paymentSection}>
            
            {loading ? (
               <Box >
                    <CircularProgress />
                    <Typography sx={styles.priceText(theme)}>
                Processing payment for {currencyFormat(price + calculateFees(price))}. Do not close until complete.
              </Typography>

                  </Box>
            )
         :(<>
         <Box>
              <Typography sx={styles.priceText(theme)}>
                {currencyFormat(price + calculateFees(price))}
              </Typography>

            </Box>
         <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!stripe || loading}
              sx={styles.paymentButton(theme)}
            >
              {loading ? "Processing..." : "Complete Payment"}
            </Button>
         </>) }
            
          </Box>
        </form>

        {/* Footer */}
        <Box sx={styles.footerContainer}>
          <Typography sx={styles.footerText(theme)}>Powered by</Typography>
          <img src={DarkLogo} alt="logo" style={styles.logo} />
        </Box>
      </Box>
    </Box>
  );
};

const StripePayment = ({
  setShowCheckout,
  price,
  currencyFormat,
  handleGetStripeClientSecret,
  handleCheckoutClick,
  setShowSuccess,
  setName,
  setPhoneNumber,
  setEmail,
  name,
  phoneNumber,
  email,
  setIsLoading,
}: CheckoutModalProps) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm
      setShowCheckout={setShowCheckout}
      price={price}
      currencyFormat={currencyFormat}
      handleGetStripeClientSecret={handleGetStripeClientSecret}
      handleCheckoutClick={handleCheckoutClick}
      setShowSuccess={setShowSuccess}
      setName={setName}
      setPhoneNumber={setPhoneNumber}
      setEmail={setEmail}
      name={name}
      phoneNumber={phoneNumber}
      email={email}
      setIsLoading={setIsLoading}
    />
  </Elements>
);

export default StripePayment;
